import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Card, CardBody, Col, Row, Table } from "reactstrap";

import { resolvePaymentGatewayLabel } from "../../../../helpers/paymentGatewayLabels";
import { buildDayClosePath } from "../../../../services/dayCloseReportService";
import { Order, OrderPayment, OrderPaymentMethod } from "../../../../services/orderService";
import { getSettings } from "../../../../services/tenantSettingService";

const normalizeOrderPayments = (payments: Order["payments"] | undefined): OrderPayment[] => {
  if (!payments) {
    return [];
  }

  if (Array.isArray(payments)) {
    return payments;
  }

  return Object.values(payments as Record<string, OrderPayment>);
};

const toLocalDateIso = (isoUtc: string, timezone: string): string => {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(isoUtc));
  } catch {
    return isoUtc.slice(0, 10);
  }
};

interface OrderPaymentDetailsCardProps {
  order: Order;
  formatMoney: (cents: number) => string;
  formatDateTime: (value: string) => string;
}

const OrderPaymentDetailsCard = ({
  order,
  formatMoney,
  formatDateTime,
}: OrderPaymentDetailsCardProps) => {
  const { t } = useTranslation();
  const [timezone, setTimezone] = useState("America/Sao_Paulo");
  const payments = normalizeOrderPayments(order.payments);
  const activePayments = payments.filter((payment) => !payment.voided_at);
  const paidAt = order.paid_at ?? activePayments[0]?.paid_at ?? null;
  const isPaid = order.status === "paid";
  const isVoided = order.status === "voided";

  useEffect(() => {
    let cancelled = false;

    getSettings()
      .then((response) => {
        if (!cancelled && response.data?.timezone) {
          setTimezone(response.data.timezone);
        }
      })
      .catch(() => {
        // keep default timezone
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!isPaid && !isVoided && activePayments.length === 0 && !paidAt) {
    return null;
  }

  const dayClosePath =
    isPaid && paidAt && order.unit_id
      ? buildDayClosePath({
          unitId: order.unit_id,
          date: toLocalDateIso(paidAt, timezone),
        })
      : null;

  return (
    <Card className="border shadow-none mb-4">
      <CardBody className="py-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h6 className="mb-0">{t("studio.orders.payment.sectionTitle")}</h6>
          {dayClosePath ? (
            <Link to={dayClosePath} className="fw-medium">
              {t("studio.orders.payment.openDayClose")}
            </Link>
          ) : null}
        </div>
        {activePayments.length > 0 ? (
          <div className="table-responsive">
            <Table size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t("studio.orders.payment.methodLabel")}</th>
                  <th>{t("studio.orders.payment.amountLabel")}</th>
                  <th>{t("studio.orders.payment.paidAtLabel")}</th>
                  <th>{t("studio.orders.payment.gatewayLabel")}</th>
                </tr>
              </thead>
              <tbody>
                {activePayments.map((payment) => {
                  const paymentMethod = payment.payment_method as OrderPaymentMethod;

                  return (
                    <tr key={payment.id}>
                      <td>{t(`studio.orders.checkout.payment.method.${paymentMethod}`)}</td>
                      <td>{formatMoney(payment.amount_cents)}</td>
                      <td>
                        {payment.paid_at
                          ? formatDateTime(payment.paid_at)
                          : t("studio.orders.common.notAvailable")}
                      </td>
                      <td>
                        {payment.gateway_slug
                          ? resolvePaymentGatewayLabel(payment.gateway_slug, t, t("studio.orders.common.notAvailable"))
                          : t("studio.orders.common.notAvailable")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : paidAt ? (
          <Row className="g-3">
            <Col sm={6} md={4}>
              <p className="text-muted mb-1">{t("studio.orders.payment.amountLabel")}</p>
              <p className="fw-medium mb-0">{formatMoney(order.total_amount)}</p>
            </Col>
            <Col sm={6} md={4}>
              <p className="text-muted mb-1">{t("studio.orders.payment.paidAtLabel")}</p>
              <p className="fw-medium mb-0">{formatDateTime(paidAt)}</p>
            </Col>
          </Row>
        ) : (
          <p className="text-muted mb-0">{t("studio.orders.payment.empty")}</p>
        )}
      </CardBody>
    </Card>
  );
};

export default OrderPaymentDetailsCard;
